import { useListOrganizationMessages } from "@package/api-client/hooks/messagesDashboardController";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/app/$appId/messages/")({
	component: MessagesPage,
});

function MessagesPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const { data, isLoading } = useListOrganizationMessages(orgId);

	const messages = data?.items ?? [];

	return (
		<div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
			<header className="mb-6">
				<p className="font-mono text-xs uppercase tracking-wide text-base-content/55">
					{t("nav.dashboard")}
				</p>
				<h1
					data-testid="messages-title"
					className="mt-1 text-2xl font-semibold tracking-tight text-base-content"
				>
					{t("messages.title")}
				</h1>
			</header>

			<div className="rounded-box border border-base-300 bg-base-100 overflow-hidden">
				<div className="overflow-x-auto">
					<table data-testid="messages-table" className="table">
						<thead>
							<tr className="font-mono text-xs uppercase tracking-wide text-base-content/55">
								<th>{t("messages.channel")}</th>
								<th>{t("messages.status")}</th>
								<th>{t("messages.recipient")}</th>
								<th>{t("messages.createdAt")}</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
								<tr>
									<td colSpan={4} className="text-center py-10">
										<span className="loading loading-spinner loading-md" />
									</td>
								</tr>
							) : messages.length === 0 ? (
								<tr>
									<td
										data-testid="messages-empty-state"
										colSpan={4}
										className="text-center text-base-content/50 py-10 font-mono text-sm"
									>
										{t("messages.noMessages")}
									</td>
								</tr>
							) : (
								messages.map((msg) => {
									const m = msg as Record<string, unknown>;
									const dest = m.destination as Record<string, unknown> | undefined;
									const to = dest?.to as string[] | undefined;
									return (
										<tr key={m.messageId as string} className="hover">
											<td>
												<span className="font-mono text-xs uppercase tracking-wide text-base-content/70">
													{m.channel as string}
												</span>
											</td>
											<td>
												<StatusPill status={m.status as string} />
											</td>
											<td className="font-mono text-xs text-base-content/80">
												{to?.join(", ") ?? "—"}
											</td>
											<td className="font-mono text-xs text-base-content/55">
												{m.createdAt ? new Date(m.createdAt as string).toLocaleString() : "—"}
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

type PillStyle = { dot: string; text: string; bg: string };

const NEUTRAL_PILL: PillStyle = {
	dot: "bg-base-content/40",
	text: "text-base-content/60",
	bg: "bg-base-200",
};

const STATUS_STYLE: Record<string, PillStyle> = {
	DELIVERED: { dot: "bg-success", text: "text-success", bg: "bg-success/10" },
	FAILED: { dot: "bg-error", text: "text-error", bg: "bg-error/10" },
	PROCESSING: { dot: "bg-primary", text: "text-primary", bg: "bg-primary/10" },
	RETRYING: { dot: "bg-warning", text: "text-warning", bg: "bg-warning/10" },
	SCHEDULED: NEUTRAL_PILL,
};

function StatusPill({ status }: { status: string }) {
	const style = STATUS_STYLE[status] ?? NEUTRAL_PILL;
	return (
		<span
			className={`inline-flex items-center gap-1.5 rounded border border-base-300 px-2 py-0.5 font-mono text-xs ${style.bg} ${style.text}`}
		>
			<span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
			{status}
		</span>
	);
}
